import { useMemo, useState } from 'react';

import { CounterpartyDetails } from 'features/deals/dealCards/CounterpartyCard';
import { ProgressBar } from 'ui-kit';
import DealButton from 'ui-kit/DealButton/DealButton';

import { useMutation } from '@apollo/client';

import { DealActionOptionType, DealRole } from '__generated__/globalTypes';
import { AdvanceDeal, AdvanceDealVariables } from 'mutation/__generated__/advanceDeal';
import { ADVANCE_DEAL } from 'mutation/advanceDeal';

import { CounterpartyCard_CounterpartyCard } from './__generated__/fragments';
import { GetTimelineVariables } from '../../__generated__/fragments';
import { GET_DEAL_TIMELINE } from '../../fragments';

type Props = {
  dealId: string;
  role: string;
} & CounterpartyCard_CounterpartyCard;

const CounterpartyCardBody = (props: Props): JSX.Element => {
  const { dealId, counterparty, is_under_review, role } = props;
  const isSeller = role === DealRole.SELLER;
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  const [advanceDeal] = useMutation<AdvanceDeal, AdvanceDealVariables>(ADVANCE_DEAL);

  const getTimelineVariables: GetTimelineVariables = useMemo(
    () => ({
      id: dealId,
    }),
    [dealId],
  );

  const handleAdvanceDeal = async (action: DealActionOptionType, actionId: string) => {
    setLoadingAction(actionId);
    try {
      await advanceDeal({
        variables: {
          id: dealId,
          action,
        },
        refetchQueries: [
          {
            query: GET_DEAL_TIMELINE,
            variables: getTimelineVariables,
          },
        ],
      });
    } catch (error) {
      // Error handling can be added here if needed
      console.error('Error advancing deal:', error);
    } finally {
      // Note: We don't reset loading state here because the component
      // should unmount/re-render when the page refreshes after the mutation
      // If for some reason it doesn't, the loading state will persist
      // which is better UX than having the button become clickable again
    }
  };

  const buttons = is_under_review && isSeller && (
    <>
      <DealButton
        variant="success"
        label="Approve Buyer Access"
        loading={loadingAction === 'approve'}
        disabled={loadingAction !== null}
        onClick={() => handleAdvanceDeal(DealActionOptionType.APPROVE_BUYER, 'approve')}
      >
        Approve Buyer Access
      </DealButton>
      <DealButton
        variant="reject"
        label="Deny Access Request"
        loading={loadingAction === 'reject'}
        disabled={loadingAction !== null}
        onClick={() => handleAdvanceDeal(DealActionOptionType.REJECT_BUYER, 'reject')}
      >
        Deny Access Request
      </DealButton>
    </>
  );

  const metrics = useMemo(
    () => ({
      'Loans/Shares': counterparty?.regulatory_metrics?.loans_shares_rank,
      Delinquency: counterparty?.regulatory_metrics?.delinquency_rank,
      'Charge Offs': counterparty?.regulatory_metrics?.charge_off_ratio_rank,
      'Net Worth Ratio': counterparty?.regulatory_metrics?.net_worth_ratio_rank,
      'Expense Ratio': counterparty?.regulatory_metrics?.expense_ratio_rank,
      ROA: counterparty?.regulatory_metrics?.roa_rank,
      ROE: counterparty?.regulatory_metrics?.roe_rank,
      'Allowance/Total Loans': counterparty?.regulatory_metrics?.allowance_total_loans_rank,
      'Efficiency Ratio': counterparty?.regulatory_metrics?.efficiency_ratio_rank,
      'IEA/Total Assets': counterparty?.regulatory_metrics?.iea_total_assets_rank,
    }),
    [counterparty],
  );

  const averageIndex = useMemo(() => {
    const ranks = Object.values(metrics).filter((rank): rank is number => rank != null);
    return ranks.length > 0 ? ranks.reduce((acc, rank) => acc + rank, 0) / ranks.length : null;
  }, [metrics]);

  return (
    <>
      <div className="flex gap-4">
        {metrics && (
          <div className="w-[400px]">
            <div>
              <div className="mb-2 flex justify-between border-b border-t border-slate-500 pb-[9px] pt-[7px]">
                <div className="flex">
                  <p className="text-sm uppercase text-slate-200">Regulatory Index</p>
                </div>
                <p className="text-sm text-slate-200">
                  {averageIndex != null ? `${averageIndex.toFixed(2)}th Index` : 'No Data'}
                </p>
              </div>
              {Object.entries(metrics).map(([label, rank]) => (
                <ProgressBar key={label} label={label} rank={rank} />
              ))}
            </div>
          </div>
        )}
        <CounterpartyDetails {...props} />
      </div>
      <div className="mt-2.5 flex flex-row gap-2.5">{buttons}</div>
    </>
  );
};

export default CounterpartyCardBody;
